import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Eye, EyeOff, Lock, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button, BrandName } from './ui';
import { authService, TenantMismatchError } from '../services/auth';
import { showToast } from '../utils/toast';
import { useTheme } from '../theme';

type AuthMode = 'signin' | 'signup' | 'forgot';

interface AuthPanelProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: AuthMode;
}

export const AuthPanel: React.FC<AuthPanelProps> = ({ isOpen, onClose, initialMode = 'signin' }) => {
  const navigate = useNavigate();
  const { branding } = useTheme();
  const ANIMATION_MS = 320;
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const resetForm = useCallback(() => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setResetSent(false);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const switchMode = useCallback((newMode: AuthMode) => {
    resetForm();
    setMode(newMode);
  }, [resetForm]);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      setMode(initialMode);
      resetForm();
      requestAnimationFrame(() => requestAnimationFrame(() => setOpen(true)));
      return;
    }

    setOpen(false);
    const t = window.setTimeout(() => setMounted(false), ANIMATION_MS);
    return () => window.clearTimeout(t);
  }, [isOpen, initialMode, resetForm, ANIMATION_MS]);

  useEffect(() => {
    if (mounted) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mounted]);

  useEffect(() => {
    if (!mounted) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mounted, handleClose]);

  const canSubmitSignIn = email && password;
  const canSubmitSignUp = email && password && password === confirmPassword && password.length >= 6;
  const canSubmitForgot = email;

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmitSignIn) return;

    try {
      setIsLoading(true);
      await authService.signInWithEmailPassword(email, password);
      handleClose();
      navigate('/home', { replace: true });
    } catch (error: any) {
      // Handle tenant mismatch - redirect to correct tenant
      if (error instanceof TenantMismatchError) {
        showToast.error(`This account belongs to '${error.storedTenant}'. Redirecting...`);
        setTimeout(() => {
          window.location.href = error.redirectUrl;
        }, 1500);
        return;
      }
      console.error('[Auth] Email/password sign-in failed', {
        code: error?.code,
        message: error?.message,
        error,
      });
      showToast.error(error?.message || 'Sign in failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmitSignUp) return;

    try {
      setIsLoading(true);
      await authService.signUpWithEmailPassword(email, password);
      handleClose();
      navigate('/home', { replace: true });
    } catch (error: any) {
      // Handle tenant mismatch - redirect to correct tenant
      if (error instanceof TenantMismatchError) {
        showToast.error(`This account belongs to '${error.storedTenant}'. Redirecting...`);
        setTimeout(() => {
          window.location.href = error.redirectUrl;
        }, 1500);
        return;
      }
      console.error('[Auth] Email/password sign-up failed', {
        code: error?.code,
        message: error?.message,
        error,
      });
      showToast.error(error?.message || 'Sign up failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmitForgot) return;

    try {
      setIsLoading(true);
      await authService.sendPasswordResetEmail(email);
      setResetSent(true);
    } catch (error: any) {
      console.error('[Auth] Forgot password failed', {
        code: error?.code,
        message: error?.message,
        error,
      });
      showToast.error(error?.message || 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogle = async () => {
    try {
      setIsLoading(true);
      await authService.signInWithGoogle();
      handleClose();
      navigate('/home', { replace: true });
    } catch (error: any) {
      // Handle tenant mismatch - redirect to correct tenant
      if (error instanceof TenantMismatchError) {
        showToast.error(`This account belongs to '${error.storedTenant}'. Redirecting...`);
        setTimeout(() => {
          window.location.href = error.redirectUrl;
        }, 1500);
        return;
      }
      console.error('[Auth] Google sign-in failed', {
        code: error?.code,
        message: error?.message,
        error,
      });
      showToast.error(error?.message || 'Google sign-in failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (!mounted) return null;

  return (
    <div className="fixed inset-0 z-[100]">
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-300 ease-out ${open ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
      />

      {/* Panel */}
      <div
        className={`absolute right-0 top-0 bottom-0 bg-white shadow-2xl flex flex-col will-change-transform will-change-opacity transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${open ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'} w-full sm:max-w-md md:max-w-xl lg:max-w-2xl lg:w-[45vw]`}
      >
        {/* Header with Branding */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            {mode === 'forgot' && (
              <button
                onClick={() => switchMode('signin')}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
                type="button"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            {/* Tenant Logo */}
            {branding.logoUrl && (
              <img src={branding.logoUrl} alt="Logo" className="w-8 h-8 rounded-lg object-contain" />
            )}
            <div>
              <h2 className="text-lg font-semibold" style={{ color: branding.textColor }}>
                {mode === 'signin' && (
                  <>
                    Sign in to <BrandName text={branding.appTitle} />
                  </>
                )}
                {mode === 'signup' && (
                  <>
                    Join <BrandName text={branding.appTitle} />
                  </>
                )}
                {mode === 'forgot' && 'Reset password'}
              </h2>
              <p className="text-xs" style={{ color: branding.textColorMuted }}>{branding.tagline}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
            type="button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {/* Google Sign In - only for signin/signup */}
          {mode !== 'forgot' && (
            <>
              <Button
                fullWidth
                variant="ghost"
                isLoading={isLoading}
                disabled={isLoading}
                onClick={handleGoogle}
                type="button"
                className="bg-white text-gray-800 border border-gray-200 hover:bg-gray-50"
              >
                <span className="flex items-center justify-center gap-2">
                  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.823 32.657 29.277 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.957 3.043l5.657-5.657C34.014 6.053 29.238 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.651-.389-3.917z" />
                    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.957 3.043l5.657-5.657C34.014 6.053 29.238 4 24 4c-7.682 0-14.39 4.337-17.694 10.691z" />
                    <path fill="#4CAF50" d="M24 44c5.169 0 9.86-1.977 13.409-5.197l-6.19-5.238C29.21 35.091 26.715 36 24 36c-5.256 0-9.79-3.319-11.279-7.946l-6.521 5.025C9.472 39.556 16.227 44 24 44z" />
                    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.709 2.013-2.037 3.695-3.794 4.826l.003-.002 6.19 5.238C36.264 39.38 44 34 44 24c0-1.341-.138-2.651-.389-3.917z" />
                  </svg>
                  <span>Continue with Google</span>
                </span>
              </Button>

              <div className="flex items-center gap-3 my-6">
                <div className="h-px bg-gray-200 flex-1" />
                <span className="text-xs text-gray-500">or</span>
                <div className="h-px bg-gray-200 flex-1" />
              </div>
            </>
          )}

          {/* Sign In Form */}
          {mode === 'signin' && (
            <form onSubmit={handleSignIn} className="space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">Email</label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    className="input-themed pl-10 pr-4"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Password</label>
                  <button
                    type="button"
                    onClick={() => switchMode('forgot')}
                    className="text-sm link-themed"
                  >
                    Forgot?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    className="input-themed pl-10 pr-12"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md hover:bg-gray-100 text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                fullWidth
                variant="primary"
                isLoading={isLoading}
                disabled={!canSubmitSignIn || isLoading}
                type="submit"
              >
                Sign in
              </Button>
            </form>
          )}

          {/* Sign Up Form */}
          {mode === 'signup' && (
            <form onSubmit={handleSignUp} className="space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">Email</label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    className="input-themed pl-10 pr-4"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">Password</label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    className="input-themed pl-10 pr-12"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md hover:bg-gray-100 text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500">Use at least 6 characters.</p>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">Confirm Password</label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    className="input-themed pl-10 pr-12"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md hover:bg-gray-100 text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {password && confirmPassword && password !== confirmPassword ? (
                  <p className="text-xs text-red-500">Passwords do not match</p>
                ) : password && confirmPassword ? (
                  <p className="text-xs text-green-600">Passwords match</p>
                ) : null}
              </div>

              <Button
                fullWidth
                variant="primary"
                isLoading={isLoading}
                disabled={!canSubmitSignUp || isLoading}
                type="submit"
              >
                Create account
              </Button>
            </form>
          )}

          {/* Forgot Password Form */}
          {mode === 'forgot' && (
            <>
              {resetSent ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Check your email</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    We've sent a password reset link to <strong>{email}</strong>
                  </p>
                  <Button variant="primary" fullWidth onClick={() => switchMode('signin')}>
                    Back to sign in
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Enter your email address and we'll send you a link to reset your password.
                  </p>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Email</label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        className="input-themed pl-10 pr-4"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        type="email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    fullWidth
                    variant="primary"
                    isLoading={isLoading}
                    disabled={!canSubmitForgot || isLoading}
                    type="submit"
                  >
                    Send reset link
                  </Button>
                </form>
              )}
            </>
          )}

          {/* Switch mode links */}
          {mode === 'signin' && (
            <p className="text-center text-sm text-gray-600 mt-6">
              Don't have an account?{' '}
              <button
                onClick={() => switchMode('signup')}
                className="link-themed font-medium"
                type="button"
              >
                Sign up
              </button>
            </p>
          )}

          {mode === 'signup' && (
            <p className="text-center text-sm text-gray-600 mt-6">
              Already have an account?{' '}
              <button
                onClick={() => switchMode('signin')}
                className="link-themed font-medium"
                type="button"
              >
                Sign in
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPanel;
